import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import POSNavbar from '../pos/POSNavbar';
import { POSProvider } from '../../context/POSContext';
import { OverrideProvider } from '../../context/OverrideContext';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { useSocket } from '../../context/SocketContext';
import { toast } from 'react-hot-toast';
import { ArrowRight } from 'lucide-react';

const ONBOARDING_STEPS = [
    { id: 1, name: 'Profil Restaurant', path: '/pos/config/details' },
    { id: 2, name: 'Règlements & TVA', path: '/pos/config/payment-methods' },
    { id: 3, name: 'Dépôts (Stockage)', path: '/pos/inventory/warehouses' },
    { id: 4, name: 'Caisses (POS)', path: '/pos/manage' },
    { id: 5, name: 'Catalogue & Produits', path: '/pos/products' },
];

const ALLOWED_PATHS = [
    '/pos/dashboard',
    '/pos/config/details',
    '/pos/config/payment-methods',
    '/pos/inventory/warehouses',
    '/pos/manage',
    '/pos/products'
];

import { usePOS } from '../../context/POSContext';
import { Navigate } from 'react-router-dom';

const POSLayoutContent = () => {
    const { socket } = useSocket();
    const location = useLocation();
    const navigate = useNavigate();
    const { onboardingStatus, loading } = usePOS();

    const onboardingStep = parseInt(sessionStorage.getItem('onboarding_step') || '0', 10);
    const activeStep = ONBOARDING_STEPS.find(s => s.id === onboardingStep);

    const handleNextOnboardingStep = () => {
        if (onboardingStep < 5) {
            const nextStep = onboardingStep + 1;
            sessionStorage.setItem('onboarding_step', nextStep.toString());
            const nextPath = ONBOARDING_STEPS.find(s => s.id === nextStep)?.path;
            if (nextPath) navigate(nextPath);
        } else {
            handleCloseOnboarding();
        }
    };

    const handleCloseOnboarding = () => {
        sessionStorage.removeItem('onboarding_step');
        navigate('/pos/dashboard');
    };

    useEffect(() => {
        if (!socket) return;

        // ponytail: no new_order toast here — SocketContext already toasts + rings for it
        // app-wide (two listeners doubled every QR order alert).
        const onMarketplaceOrder = (data: any) => {
            // Play a synthetic beep for attention
            try {
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
                if (audioCtx.state === 'suspended') {
                    audioCtx.resume();
                }
                const oscillator = audioCtx.createOscillator();
                const gainNode = audioCtx.createGain();
                oscillator.type = 'triangle';
                oscillator.frequency.setValueAtTime(800, audioCtx.currentTime); // High pitch notification
                oscillator.frequency.exponentialRampToValueAtTime(1200, audioCtx.currentTime + 0.1);
                
                gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);

                oscillator.connect(gainNode);
                gainNode.connect(audioCtx.destination);
                oscillator.start();
                oscillator.stop(audioCtx.currentTime + 0.5);
            } catch (e) {
                console.warn('Could not play notification sound:', e);
            }

            const sourceDisplay = data.source === 'glovo' ? '🛵 Glovo' : data.source === 'yassir' ? '🛵 Yassir' : `Livraison (${data.source})`;
            toast(`Nouvelle Commande ${sourceDisplay}: ${data.orderNumber}`, {
                duration: 15000,
                icon: '🔥',
                style: {
                    background: data.source === 'glovo' ? '#FECE00' : data.source === 'yassir' ? '#E91E63' : '#333',
                    color: data.source === 'glovo' ? '#000' : '#fff',
                    fontWeight: 'bold',
                    fontSize: '1.2em'
                }
            });
        };
        socket.on('new_marketplace_order', onMarketplaceOrder);

        return () => {
            // By reference: a bare off('new_order') here used to also strip SocketContext's listener.
            socket.off('new_marketplace_order', onMarketplaceOrder);
        };
    }, [socket]);

    // Force redirection if onboarding is incomplete
    if (!loading && onboardingStatus && !onboardingStatus.isComplete) {
        const isAllowed = ALLOWED_PATHS.some(p => location.pathname.startsWith(p)) || location.pathname === '/pos/dashboard';
        if (!isAllowed) {
            toast.error("Veuillez terminer la configuration de votre restaurant (Caisses, Dépôts, etc.) avant de naviguer dans le système.", { id: 'onboarding_error' });
            return <Navigate to="/pos/dashboard" replace />;
        }
    }

    return (
        <OverrideProvider>
            <div className="min-h-screen bg-gray-50 font-sans">
                {/* Common POS Navigation */}
                <POSNavbar />
                
                {/* Onboarding Banner */}
                {activeStep && (
                    <div className="bg-indigo-600 text-white shadow-md sticky top-16 z-40">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-800 text-xs font-black text-white shrink-0">
                                    {activeStep.id}
                                </span>
                                <span className="text-sm font-semibold tracking-wide">
                                    Configuration : <span className="font-bold text-indigo-200">{activeStep.name}</span>
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={handleCloseOnboarding}
                                    className="text-xs font-medium text-indigo-200 hover:text-white transition-colors cursor-pointer"
                                >
                                    Quitter l'assistant
                                </button>
                                <button 
                                    onClick={handleNextOnboardingStep}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-indigo-700 hover:bg-indigo-50 rounded-lg text-xs font-bold transition-all shadow-sm cursor-pointer"
                                >
                                    {onboardingStep < 5 ? 'Étape suivante' : 'Terminer'} 
                                    <ArrowRight size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content Area */}
                <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <ErrorBoundary>
                        <Outlet />
                    </ErrorBoundary>
                </main>
            </div>
        </OverrideProvider>
    );
};

const POSLayout = () => {
    return (
        <POSProvider>
            <POSLayoutContent />
        </POSProvider>
    );
};

export default POSLayout;
